class LotrBackgroundWrapper extends HTMLElement {
  static CUSTOM_ELEMENT_NAME = 'lotr-background-wrapper';
  static WRAPPER_SELECTOR = `${LotrBackgroundWrapper.CUSTOM_ELEMENT_NAME}, [data-lotr-background-wrapper]`;
  static SHOPIFY_SECTION_SELECTOR = '.shopify-section, [id^="shopify-section-"]';
  static LAYER_CLASS = 'lotr-background-wrapper__layer';
  static TARGET_CLASS = 'lotr-background-wrapper__target';
  static TRANSPARENT_TARGET_CLASS = 'lotr-background-wrapper__target--transparent';
  static INIT_ALL_DEBOUNCE_MS = 120;
  static initAllTimer = null;
  static resizeListenerBound = false;

  constructor() {
    super();
    this.resizeObserver = null;
    this.resizeFrame = null;
    this.targets = [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  static getSectionRoot(node) {
    return node.closest(LotrBackgroundWrapper.SHOPIFY_SECTION_SELECTOR) || node;
  }

  static parseCount(value) {
    const count = Number.parseInt(value, 10);
    if (Number.isNaN(count) || count < 1) {
      return 1;
    }

    return count;
  }

  static matchesTarget(sectionRoot, selector) {
    const trimmed = (selector || '').trim();
    if (!trimmed) {
      return true;
    }

    try {
      return sectionRoot.matches(trimmed) || Boolean(sectionRoot.querySelector(trimmed));
    } catch (error) {
      console.warn('[lotr-background-wrapper] Invalid target selector:', trimmed, error);
      return false;
    }
  }

  static resetWrapper(wrapperRoot) {
    if (!wrapperRoot) return;

    wrapperRoot.classList.remove(LotrBackgroundWrapper.LAYER_CLASS);
    wrapperRoot.style.top = '';
    wrapperRoot.style.height = '';
    wrapperRoot.style.minHeight = '';
    wrapperRoot.style.removeProperty('--lbw-layer-height');
  }

  static resetTargets(targets) {
    if (!targets?.length) return;

    targets.forEach((section) => {
      section.classList.remove(LotrBackgroundWrapper.TARGET_CLASS);
      section.classList.remove(LotrBackgroundWrapper.TRANSPARENT_TARGET_CLASS);
    });
  }

  static collectTargets(wrapperRoot, count, targetSelector) {
    const targets = [];
    let nextSection = wrapperRoot.nextElementSibling;

    while (targets.length < count) {
      if (!nextSection || !nextSection.matches(LotrBackgroundWrapper.SHOPIFY_SECTION_SELECTOR)) {
        break;
      }

      if (!LotrBackgroundWrapper.matchesTarget(nextSection, targetSelector)) {
        break;
      }

      targets.push(nextSection);
      nextSection = nextSection.nextElementSibling;
    }

    return targets;
  }

  static applyWrapperBounds(wrapperRoot, targets) {
    if (!wrapperRoot?.parentElement || !targets.length) {
      return;
    }

    const parent = wrapperRoot.parentElement;
    const parentPosition = window.getComputedStyle(parent).position;
    if (parentPosition === 'static') {
      parent.style.position = 'relative';
    }

    const parentRect = parent.getBoundingClientRect();
    const firstRect = targets[0].getBoundingClientRect();
    const lastRect = targets[targets.length - 1].getBoundingClientRect();
    const top = firstRect.top - parentRect.top;
    const height = lastRect.bottom - firstRect.top;
    const currentTop = Number.parseFloat(wrapperRoot.style.top) || 0;
    const currentHeight = Number.parseFloat(wrapperRoot.style.height) || 0;

    if (Math.abs(currentTop - top) < 0.5 && Math.abs(currentHeight - height) < 0.5) {
      return;
    }

    wrapperRoot.style.top = `${top}px`;
    wrapperRoot.style.height = `${height}px`;
    wrapperRoot.style.minHeight = `${height}px`;
    wrapperRoot.style.setProperty('--lbw-layer-height', `${height}px`);
  }

  init() {
    const wrapperRoot = LotrBackgroundWrapper.getSectionRoot(this);

    if (!wrapperRoot?.parentElement) {
      return;
    }

    this.disconnectResizeObserver();
    LotrBackgroundWrapper.resetTargets(this.targets);
    LotrBackgroundWrapper.resetWrapper(wrapperRoot);

    const count = LotrBackgroundWrapper.parseCount(this.getAttribute('data-lotr-background-section-count'));
    const targetSelector = this.getAttribute('data-lotr-background-target-selector') || '';
    const targets = LotrBackgroundWrapper.collectTargets(wrapperRoot, count, targetSelector);
    const shouldHideChildBackgrounds = this.classList.contains('lotr-background-wrapper--hide-child-backgrounds');

    if (!targets.length) {
      wrapperRoot.style.display = 'none';
      this.removeAttribute('data-lotr-background-initialized');
      this.targets = [];
      return;
    }

    wrapperRoot.style.display = '';
    wrapperRoot.classList.add(LotrBackgroundWrapper.LAYER_CLASS);

    targets.forEach((target) => {
      target.classList.add(LotrBackgroundWrapper.TARGET_CLASS);
      if (shouldHideChildBackgrounds) {
        target.classList.add(LotrBackgroundWrapper.TRANSPARENT_TARGET_CLASS);
      }
    });

    this.targets = targets;
    this.applyBounds();
    this.observeTargetSizes();
    this.setAttribute('data-lotr-background-initialized', 'true');
  }

  destroy() {
    const wrapperRoot = LotrBackgroundWrapper.getSectionRoot(this);

    this.disconnectResizeObserver();
    LotrBackgroundWrapper.resetTargets(this.targets);
    LotrBackgroundWrapper.resetWrapper(wrapperRoot);
    this.targets = [];
    this.removeAttribute('data-lotr-background-initialized');
  }

  applyBounds() {
    LotrBackgroundWrapper.applyWrapperBounds(LotrBackgroundWrapper.getSectionRoot(this), this.targets);
  }

  disconnectResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
    }
  }

  observeTargetSizes() {
    if (typeof ResizeObserver === 'undefined' || !this.targets.length) {
      return;
    }

    this.disconnectResizeObserver();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeFrame) return;

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.applyBounds();
      });
    });

    this.targets.forEach((target) => {
      this.resizeObserver.observe(target);
    });
  }

  static getWrappers(root = document) {
    if (root.matches?.(LotrBackgroundWrapper.WRAPPER_SELECTOR)) {
      return [root];
    }

    return Array.from(root.querySelectorAll?.(LotrBackgroundWrapper.WRAPPER_SELECTOR) || []);
  }

  static initializeAll(root = document) {
    LotrBackgroundWrapper.getWrappers(root).forEach((wrapper) => {
      if (typeof wrapper.init === 'function') {
        wrapper.init();
      }
    });
  }

  static cancelScheduledInitAll() {
    if (!LotrBackgroundWrapper.initAllTimer) return;

    window.clearTimeout(LotrBackgroundWrapper.initAllTimer);
    LotrBackgroundWrapper.initAllTimer = null;
  }

  static scheduleInitAll() {
    LotrBackgroundWrapper.cancelScheduledInitAll();

    LotrBackgroundWrapper.initAllTimer = window.setTimeout(() => {
      LotrBackgroundWrapper.initAllTimer = null;
      LotrBackgroundWrapper.initializeAll();
    }, LotrBackgroundWrapper.INIT_ALL_DEBOUNCE_MS);
  }

  static destroyAllIn(root) {
    LotrBackgroundWrapper.cancelScheduledInitAll();

    LotrBackgroundWrapper.getWrappers(root).forEach((wrapper) => {
      if (typeof wrapper.destroy === 'function') {
        wrapper.destroy();
      }
    });
  }

  static register() {
    if (window.customElements.get(LotrBackgroundWrapper.CUSTOM_ELEMENT_NAME)) return;

    window.customElements.define(LotrBackgroundWrapper.CUSTOM_ELEMENT_NAME, LotrBackgroundWrapper);
  }

  static bindGlobalListeners() {
    if (LotrBackgroundWrapper.resizeListenerBound) return;

    LotrBackgroundWrapper.resizeListenerBound = true;
    window.addEventListener('resize', () => LotrBackgroundWrapper.scheduleInitAll());
  }

  static bootstrap() {
    LotrBackgroundWrapper.register();
    LotrBackgroundWrapper.bindGlobalListeners();
    LotrBackgroundWrapper.initializeAll();
  }
}

LotrBackgroundWrapper.register();
LotrBackgroundWrapper.bindGlobalListeners();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => LotrBackgroundWrapper.initializeAll());
} else {
  LotrBackgroundWrapper.initializeAll();
}

document.addEventListener('shopify:section:load', (event) => {
  LotrBackgroundWrapper.cancelScheduledInitAll();
  LotrBackgroundWrapper.initializeAll(event.target);
  LotrBackgroundWrapper.scheduleInitAll();
});

document.addEventListener('shopify:section:reorder', () => LotrBackgroundWrapper.scheduleInitAll());

document.addEventListener('shopify:block:select', () => LotrBackgroundWrapper.scheduleInitAll());

document.addEventListener('shopify:section:unload', (event) => {
  LotrBackgroundWrapper.destroyAllIn(event.target);
});
